import { getApp, getApps, initializeApp, type FirebaseOptions } from 'firebase/app';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { FUNCTIONS_REGION } from './region';

declare const __FIREBASE_WEBAPP_CONFIG__: string;

/**
 * The web half of the Firebase configuration.
 *
 * App Hosting injects FIREBASE_WEBAPP_CONFIG at build time once a Firebase web
 * app is associated with the backend, and vite.config.ts bakes it in as
 * __FIREBASE_WEBAPP_CONFIG__. Local dev has no such variable, so the VITE_*
 * values from .env.local fill in. Reading both means one file serves both
 * places, with no second configuration path to keep in sync.
 */
function resolveConfig(): FirebaseOptions {
  const baked = typeof __FIREBASE_WEBAPP_CONFIG__ === 'string' ? __FIREBASE_WEBAPP_CONFIG__ : '';
  if (baked) {
    try {
      return JSON.parse(baked) as FirebaseOptions;
    } catch {
      // A malformed injection must not fall through to a half-configured app
      // that fails later as an unrelated auth error.
      throw new Error('FIREBASE_WEBAPP_CONFIG is not valid JSON');
    }
  }
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
}

export const app = getApps().length ? getApp() : initializeApp(resolveConfig());
export const db = getFirestore(app);

/**
 * Point the app at the local emulators, so the whole UI can be walked without
 * spending a verification code — the app had never once run against a live
 * backend, because every route past sign-in needs an OTP.
 *
 * Guarded by BOTH the dev build and an explicit flag. `import.meta.env.DEV` is
 * false in every production build, so this branch is removed from the bundle
 * entirely; the flag then means a developer has to ask for it on purpose. A
 * bypass that could be switched on in production is not a bypass, it is a
 * door.
 */
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === '1') {
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectAuthEmulator(getAuth(app), 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFunctionsEmulator(getFunctions(app, FUNCTIONS_REGION), '127.0.0.1', 5001);
  // eslint-disable-next-line no-console
  console.info('[fusilat] using local emulators');
}
